import React, { useState } from 'react';
import { 
    AppBar, 
    Toolbar, 
    Typography, 
    Button, 
    IconButton, 
    Menu, 
    MenuItem, 
    Divider,
    Avatar,
    Box,
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import ChatIcon from '@mui/icons-material/Chat';
import CreateIcon from '@mui/icons-material/Create';
import GroupsIcon from '@mui/icons-material/Groups';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import { ReactComponent as CommunityIcon } from '../../icons/community-icon.svg';
import axios from 'axios';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';

const Navbar = () => {
    const [anchorEl, setAnchorEl] = useState(null);
    const { isAuthenticated, setIsAuthenticated, user } = useAuth();
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Function to get user's initials for avatar fallback
    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ')
            .map(word => word[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const handleMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMenuClick = (path) => {
        handleClose();
        navigate(path);
    };

    const handleLogout = () => {
        try {
            localStorage.clear();
            setIsAuthenticated(false);
            delete axios.defaults.headers.common['Authorization'];
            handleClose();
            navigate('/auth');
        } catch (error) {
            // Handle error silently or show user-friendly message
        }
    };

    const buttonStyle = {
        mx: 1,
        color: '#ffffff',
        borderRadius: '20px',
        border: '2px solid rgba(255,255,255,0.8)',
        padding: '6px 16px',
        textTransform: 'none',
        fontSize: '15px',
        background: 'rgba(255,255,255,0.1)',
        backdropFilter: 'blur(5px)',
        '&:hover': {
            background: 'rgba(255,255,255,0.2)',
            borderColor: '#ffffff'
        }
    };

    const handleMobileMenuToggle = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const renderMobileMenu = () => (
        <Drawer
            anchor="right"
            open={mobileMenuOpen}
            onClose={handleMobileMenuToggle}
            PaperProps={{
                sx: {
                    width: 240,
                    background: 'linear-gradient(160deg, #4A3428 0%, #3A281F 100%)',
                }
            }}
        >
            <Box sx={{ p: 2 }}>
                {user && (
                    <>
                        <Avatar
                            src={user?.imageUrl}
                            alt={user?.name || 'User'}
                            sx={{
                                width: 40,
                                height: 40,
                                mx: 'auto',
                                mb: 1,
                                bgcolor: user?.imageUrl ? 'transparent' : 'rgba(255,255,255,0.2)',
                                border: '2px solid rgba(255,255,255,0.8)',
                            }}
                        >
                            {!user?.imageUrl && getInitials(user?.name)}
                        </Avatar>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                color: 'white',
                                textAlign: 'center',
                                mb: 2
                            }}
                        >
                            {user.name || user.email}
                        </Typography>
                    </>
                )}
            </Box>

            <List>
                {isAuthenticated && (
                    <>
                        <ListItem 
                            button 
                            component={Link} 
                            to="/"
                            onClick={handleMobileMenuToggle}
                        >
                            <ListItemIcon sx={{ color: 'white' }}>
                                <ChatIcon />
                            </ListItemIcon>
                            <ListItemText 
                                primary="Here for You" 
                                sx={{ color: 'white' }}
                            />
                        </ListItem>
                        <ListItem
                            button
                            component={Link}
                            to="/daily-uplift"
                            onClick={handleMobileMenuToggle}
                        >
                            <ListItemIcon sx={{ color: 'white' }}>
                                <WhatshotIcon />
                            </ListItemIcon>
                            <ListItemText primary="Daily Uplift" sx={{ color: 'white' }} />
                        </ListItem>
                        <ListItem 
                            button 
                            component={Link} 
                            to="/create"
                            onClick={handleMobileMenuToggle}
                        >
                            <ListItemIcon sx={{ color: 'white' }}>
                                <CreateIcon />
                            </ListItemIcon>
                            <ListItemText 
                                primary="Post Tale" 
                                sx={{ color: 'white' }}
                            />
                        </ListItem>
                        <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />
                    </>
                )}
                <ListItem 
                    button 
                    component={Link} 
                    to="/tales"
                    onClick={handleMobileMenuToggle}
                >
                    <ListItemIcon sx={{ color: 'white' }}>
                        <MenuBookIcon />
                    </ListItemIcon>
                    <ListItemText 
                        primary="Tales" 
                        sx={{ color: 'white' }}
                    />
                </ListItem>
                <ListItem 
                    button 
                    component={Link} 
                    to="/groups"
                    onClick={handleMobileMenuToggle}
                >
                    <ListItemIcon sx={{ color: 'white' }}>
                        <GroupsIcon />
                    </ListItemIcon>
                    <ListItemText 
                        primary="Groups" 
                        sx={{ color: 'white' }}
                    />
                </ListItem>
                <ListItem 
                    button 
                    component={Link} 
                    to="/resources"
                    onClick={handleMobileMenuToggle}
                >
                    <ListItemIcon sx={{ color: 'white' }}>
                        <LibraryBooksIcon />
                    </ListItemIcon>
                    <ListItemText 
                        primary="Resources" 
                        sx={{ color: 'white' }}
                    />
                </ListItem>
                {!isAuthenticated && (
                    <>
                        <ListItem 
                            button 
                            component={Link} 
                            to="/use-cases"
                            onClick={handleMobileMenuToggle}
                        >
                            <ListItemIcon sx={{ color: 'white' }}>
                                <LightbulbOutlinedIcon />
                            </ListItemIcon>
                            <ListItemText 
                                primary="Use Cases" 
                                sx={{ color: 'white' }}
                            />
                        </ListItem>
                        <ListItem 
                            button 
                            component={Link} 
                            to="/about"
                            onClick={handleMobileMenuToggle}
                        >
                            <ListItemIcon sx={{ color: 'white' }}>
                                <InfoOutlinedIcon />
                            </ListItemIcon>
                            <ListItemText 
                                primary="About" 
                                sx={{ color: 'white' }}
                            />
                        </ListItem>
                    </>
                )}
                <ListItem 
                    button 
                    component={Link} 
                    to="/contact"
                    onClick={handleMobileMenuToggle}
                >
                    <ListItemIcon sx={{ color: 'white' }}>
                        <ContactSupportIcon />
                    </ListItemIcon>
                    <ListItemText 
                        primary="Contact" 
                        sx={{ color: 'white' }}
                    />
                </ListItem>

                {!isAuthenticated && (
                    <>
                        <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />
                        <ListItem 
                            button 
                            component={Link} 
                            to="/auth"
                            onClick={handleMobileMenuToggle}
                        >
                            <ListItemIcon sx={{ color: 'white' }}>
                                <PersonIcon />
                            </ListItemIcon>
                            <ListItemText 
                                primary="Sign In" 
                                sx={{ color: 'white' }}
                            />
                        </ListItem>
                    </>
                )}

                {isAuthenticated && (
                    <>
                        <ListItem 
                            button 
                            component={Link} 
                            to="/account"
                            onClick={handleMobileMenuToggle}
                        >
                            <ListItemIcon sx={{ color: 'white' }}>
                                <PersonIcon />
                            </ListItemIcon>
                            <ListItemText 
                                primary="Account" 
                                sx={{ color: 'white' }}
                            />
                        </ListItem>
                        <ListItem 
                            button 
                            component={Link} 
                            to="/settings"
                            onClick={handleMobileMenuToggle}
                        >
                            <ListItemIcon sx={{ color: 'white' }}>
                                <SettingsIcon />
                            </ListItemIcon>
                            <ListItemText 
                                primary="Settings" 
                                sx={{ color: 'white' }}
                            />
                        </ListItem>
                        <ListItem 
                            button 
                            onClick={() => {
                                handleLogout();
                                handleMobileMenuToggle();
                            }}
                            sx={{
                                color: '#ff4d4d'
                            }}
                        >
                            <ListItemIcon sx={{ color: '#ff4d4d' }}>
                                <LogoutIcon />
                            </ListItemIcon>
                            <ListItemText primary="Logout" />
                        </ListItem>
                    </>
                )}
            </List>
        </Drawer>
    );

    const menuButtons = (
        <Box sx={{ 
            display: { xs: 'none', md: 'flex' }, 
            alignItems: 'center',
            gap: 1,
            ml: 'auto'
        }}>
            {isAuthenticated && (
                <>
                    <Button
                        component={Link}
                        to="/"
                        startIcon={<ChatIcon />}
                        sx={{
                            ...buttonStyle,
                            background: 'rgba(255,255,255,0.15)',
                            borderColor: '#ffffff',
                            '&:hover': {
                                background: 'rgba(255,255,255,0.25)',
                                borderColor: '#ffffff'
                            }
                        }}
                    >
                        Here for You
                    </Button>
                    <Button
                        component={Link}
                        to="/daily-uplift"
                        startIcon={<WhatshotIcon />}
                        sx={buttonStyle}
                    >
                        Daily Uplift
                    </Button>
                    <Button
                        component={Link}
                        to="/create"
                        startIcon={<CreateIcon />}
                        sx={buttonStyle}
                    >
                        Post Tale
                    </Button>
                </>
            )}
            <Button 
                component={Link} 
                to="/groups"
                startIcon={<GroupsIcon />}
                sx={buttonStyle}
            >
                Groups
            </Button>
            <Button 
                component={Link} 
                to="/tales"
                startIcon={<MenuBookIcon />}
                sx={buttonStyle}
            >
                Tales
            </Button>
            <Button 
                component={Link} 
                to="/resources"
                startIcon={<LibraryBooksIcon />}
                sx={buttonStyle}
            >
                Resources
            </Button>
            {!isAuthenticated && (
                <>
                    <Button 
                        component={Link} 
                        to="/use-cases"
                        startIcon={<LightbulbOutlinedIcon />}
                        sx={buttonStyle}
                    >
                        Use Cases
                    </Button>
                    <Button 
                        component={Link} 
                        to="/about"
                        startIcon={<InfoOutlinedIcon />}
                        sx={buttonStyle}
                    >
                        About
                    </Button>
                </>
            )}
            <Button 
                component={Link} 
                to="/contact"
                startIcon={<ContactSupportIcon />}
                sx={buttonStyle}
            >
                Contact
            </Button>

            {isAuthenticated ? (
                <>
                    <IconButton
                        onClick={handleMenu}
                        sx={{ 
                            ml: 1,
                            padding: 0.5
                        }}
                    >
                        <Avatar
                            src={user?.imageUrl}
                            alt={user?.name || 'User'}
                            sx={{
                                width: 35,
                                height: 35,
                                bgcolor: user?.imageUrl ? 'transparent' : 'rgba(255,255,255,0.2)',
                                fontSize: '14px',
                                fontWeight: 500,
                                border: '2px solid rgba(255,255,255,0.8)',
                                color: '#ffffff',
                                '&:hover': {
                                    borderColor: '#ffffff',
                                    transform: 'scale(1.05)',
                                    transition: 'all 0.2s ease'
                                }
                            }}
                        >
                            {!user?.imageUrl && getInitials(user?.name)}
                        </Avatar>
                    </IconButton>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                        PaperProps={{
                            elevation: 3,
                            sx: {
                                mt: 1,
                                minWidth: '200px',
                                borderRadius: '8px',
                                '& .MuiMenuItem-root': {
                                    py: 1,
                                    px: 2
                                }
                            }
                        }}
                    >
                        {user && (
                            <MenuItem 
                                sx={{ 
                                    opacity: 0.7,
                                    pointerEvents: 'none',
                                    pb: 2
                                }}
                            >
                                <Typography variant="body2">
                                    Signed in as <strong>{user.name || user.email}</strong>
                                </Typography>
                            </MenuItem>
                        )}
                        <Divider sx={{ mb: 1 }} />
                        <MenuItem 
                            onClick={() => handleMenuClick('/account')}
                            sx={{
                                '&:hover': { color: '#6c5ce7' }
                            }}
                        >
                            <PersonIcon sx={{ mr: 1.5 }} />
                            Account
                        </MenuItem>
                        <MenuItem 
                            onClick={() => handleMenuClick('/settings')}
                            sx={{
                                '&:hover': { color: '#6c5ce7' }
                            }}
                        >
                            <SettingsIcon sx={{ mr: 1.5 }} />
                            Settings
                        </MenuItem>
                        <Divider sx={{ my: 1 }} />
                        <MenuItem 
                            onClick={handleLogout}
                            sx={{
                                color: '#dc3545',
                                '&:hover': { 
                                    backgroundColor: 'rgba(220, 53, 69, 0.1)' 
                                }
                            }}
                        >
                            <LogoutIcon sx={{ mr: 1.5 }} />
                            Logout
                        </MenuItem>
                    </Menu>
                </>
            ) : (
                <Button
                    component={Link}
                    to="/auth"
                    sx={buttonStyle}
                >
                    Login
                </Button>
            )}
        </Box>
    );

    const mobileMenuButton = (
        <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleMobileMenuToggle}
            sx={{ 
                ml: 'auto',
                display: { md: 'none' }
            }}
        >
            <MenuIcon />
        </IconButton>
    );

    return (
        <AppBar 
            position="fixed" 
            sx={{ 
                background: 'linear-gradient(160deg, #4A3428 0%, #3A281F 100%)',
                boxShadow: '0 1px 0 rgba(42, 34, 28, 0.14)',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
            }}
        >
            <Toolbar>
                <Box 
                    sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        mr: 2
                    }}
                >
                    <IconButton
                        component={Link}
                        to="/"
                        sx={{ 
                            p: 1.5,
                            '&:hover': {
                                background: 'rgba(255,255,255,0.1)'
                            },
                            '& svg': {
                                width: '52px',
                                height: '52px',
                                '& path, & circle': {
                                    color: 'inherit',
                                }
                            }
                        }}
                        disableRipple
                    >
                        <CommunityIcon style={{ 
                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                        }} />
                    </IconButton>
                    <Typography 
                        variant="h6" 
                        component={Link} 
                        to={isAuthenticated ? "/" : "/tales"}
                        sx={{ 
                            textDecoration: 'none',
                            color: 'rgba(255,255,255,0.95)',
                            fontWeight: 500,
                            letterSpacing: '0.5px',
                            textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                            ml: 1,
                            fontSize: '1.5rem'
                        }}
                    >
                        Rejection Tales
                    </Typography>
                </Box>

                {isMobile ? mobileMenuButton : menuButtons}
                {renderMobileMenu()}
            </Toolbar>
        </AppBar>
    );
};

export default Navbar;